import { Fragment, useEffect, useState } from 'react';
import { getTutorials } from '../../../../store/tutorials';
import { tutorialConstants } from '../../../../utils/constants';
import { HelideckDropTestItem, HelideckNetDropSummary, HelideckVIItem, PDFCoverSheetProps, PDFImageProps, VTEquipmentAnomalyData } from '../../../../utils/models';
import { useAppSelector } from '../../../../store/configureStore';

import Button from '../../../themed/button';
import Elevation from '../../../themed/instruction-modals/elevation';
import ETAnomalySummary from '../et/etAnomalySummary';
import HelideckCoverForm from '../../../forms/helideckCoverForm';
import HelideckDrawingSheetForm from '../../../forms/HelideckDrawingSheetForm';
import HelideckTiePhotoForm from '../../../forms/helideck-tie-down/helideckTiePhotoForm';
import HelideckDropSketchForm from '../../../forms/helideck-net-drop/helideckDropSketchForm';
import HelideckNetDropTestReport from '.';
import HelideckDropSummaryForm from '../../../forms/helideck-net-drop/helideckDropSummaryForm';
import HelideckTieTestItemsForm from '../../../forms/helideck-tie-down/helideckTieTestItemsForm';
import HelideckVIItemsForm from '../../../forms/helideck-net-drop/helideckVIItemsForm';
import Modal from '../../../themed/modal';
import Zippy from '../../../themed/zippy';

import useAutoSaveReport from '../../../../hooks/useAutoSave';
import useProjectDetails from '../../../../hooks/useProjectDetails';

const CreateHelideckTieDownReport = () => {
	const [anomalyData, setAnomalyData] = useState<VTEquipmentAnomalyData[]>([]);
	const [coverSheetData, setCoverSheetData] = useState<PDFCoverSheetProps>();
	const [summaryData, setSummaryData] = useState<HelideckNetDropSummary>();
	const [drawingSheetData, setDrawingSheetData] = useState<PDFImageProps[]>([]);
	const [sketchData, setSketchData] = useState<PDFImageProps[]>([]);
	const [viItems, setVIItems] = useState<HelideckVIItem[]>([]);
	const [tdItems, setTDItems] = useState<HelideckDropTestItem[]>([]);
	const [photographicData, setPhotographicData] = useState<any>([]);
	const [previewReport, setPreviewReport] = useState<boolean>(false);

	const togglePreviewReport = (): void => {
		setPreviewReport((p: boolean) => !p);
	};

	const getReportDetails = () => {
		return {
			anomalyData,
			coverSheetData,
			summarySheetData: summaryData,
			drawingSheetPhotos: drawingSheetData,
			sketchData,
			viTableData: viItems,
			tdTableData: tdItems,
			photographicRecords: photographicData,
			projectDetails,
		};
	};

	const { projectDetails, error } = useProjectDetails()
	const { activeTutorial } = useAppSelector(getTutorials);

	const reportDetails = getReportDetails();
	const autoSave = useAutoSaveReport(reportDetails);

	useEffect(() => {
		type ReportData = ReturnType<typeof getReportDetails>;
		const cachedReport = autoSave.oldReportData as ReportData | null;

		if (cachedReport) {
			setAnomalyData(cachedReport.anomalyData);
			setCoverSheetData(cachedReport.coverSheetData);
			setSummaryData(cachedReport.summarySheetData);
			setPhotographicData(cachedReport.photographicRecords);
			setSketchData(cachedReport.sketchData);
			setVIItems(cachedReport.viTableData);
			setTDItems(cachedReport.tdTableData);
			setDrawingSheetData(cachedReport.drawingSheetPhotos);
		}
	}, [autoSave.oldReportData]);

	return (
		<Fragment>
			<Modal isVisible={previewReport} onClose={togglePreviewReport}>
				<HelideckNetDropTestReport {...reportDetails} />
			</Modal>

			<Fragment>
				<Zippy headerTitle="Cover Sheet" isValid={!!coverSheetData}>
					<HelideckCoverForm onProceed={setCoverSheetData} />
				</Zippy>

				<Zippy headerTitle="Summary Sheet" isValid={!!summaryData}>
					<HelideckDropSummaryForm onProceed={setSummaryData} />
				</Zippy>

				<Zippy headerTitle="Visual Inspection Report" isValid={viItems.length > 0}>
					<HelideckVIItemsForm onProceed={setVIItems} />
				</Zippy>

				<Zippy headerTitle="Drawing Sheet" isValid={drawingSheetData.length > 0}>
					<HelideckDrawingSheetForm onProceed={setDrawingSheetData} />
				</Zippy>

				<Zippy headerTitle="Inspection Sketch" isValid={sketchData.length > 0}>
					<HelideckDropSketchForm onProceed={setSketchData} />
				</Zippy>

				<Zippy headerTitle="Tie Down Test Result" isValid={tdItems.length > 0}>
					<HelideckTieTestItemsForm onProceed={setTDItems} />
				</Zippy>

				<Zippy headerTitle="Anomaly Data" isValid={anomalyData.length > 0}>
					<ETAnomalySummary
						anomalyData={anomalyData}
						onDataChange={setAnomalyData}
					/>
				</Zippy>

				<Zippy headerTitle="Photographic Record" isValid={photographicData.length > 0}>
					<HelideckTiePhotoForm onProceed={setPhotographicData} />
				</Zippy>

				<div className="d-flex justify-content-end">
					<Elevation isElevated={activeTutorial === tutorialConstants.PREVIEW_REPORT}>
						<Button
							label="Save and Preview Report"
							disabled={error}
							onClick={() => {
								autoSave.saveReport();
								togglePreviewReport();
							}}
						/>
					</Elevation>
				</div>
			</Fragment>
		</Fragment>
	);
};

export default CreateHelideckTieDownReport;
